import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useStomp } from 'hooks/useStomp';
import { logout, updateLocAlim } from 'services/api-service/center';
import { persistor } from 'store';

interface HeaderProps {
  title?: string;
  userinfo?: any;
  onInitialize: () => void;
  onWarning: (message: any) => void;
  isCenter?: boolean;
}

const STOMP_CONFIG = {
  brokerUrl: 'https://soritok.kr/chatting/ws-stomp',
  useSockJS: true,
};

const CenterHeader = ({ title, userinfo, onInitialize, onWarning, isCenter = false }: HeaderProps) => {
  const navigate = useNavigate();

  const onWarningRef = useRef(onWarning);
  useEffect(() => {
    onWarningRef.current = onWarning;
  });

  const token = sessionStorage.getItem('token') || '';
  const connectHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const soritok = useStomp({
    ...STOMP_CONFIG,
    connectHeaders,
  });

  useEffect(() => {
    const unsubscribeAlert = soritok.subscribe<any>('/exchange/chat.exchange/room.rescue', async (rawMessage) => {
      if (!rawMessage) return;

      let parsedMessage = rawMessage;

      if (typeof rawMessage === 'string') {
        try {
          parsedMessage = JSON.parse(rawMessage);
        } catch (e) {
          console.error('JSON 파싱 실패:', e, rawMessage);
          return;
        }
      }

      try {
        const res = await updateLocAlim(
          undefined,
          undefined,
          parsedMessage.shipName,
          parsedMessage.name,
          parsedMessage.phoneNumber,
          parsedMessage.lastReceivedTime,
          parsedMessage.lastLocLat,
          parsedMessage.lastLocLon,
          null,
          parsedMessage.type
        );

        const serverIdx = res?.raiIdx || null;

        const enhancedMessage = {
          ...parsedMessage,
          raiIdx: serverIdx,
        };

        onWarningRef.current?.(enhancedMessage);
      } catch (error) {
        console.error('소실 정보 저장 실패:', error);
      }
    });

    return () => {
      unsubscribeAlert();
    };
  }, [soritok.subscribe]);

  const handleReset = () => {
    soritok.publish('/exchange/chat.exchange/room.rescue', {
      type: 'rescue',
      shipName: '네모호',
      name: '네모선장',
      phoneNumber: '01045831772',
      lastReceivedTime: '2026-06-22 13:55:53.856',
      lastLocLat: '34.1234',
      lastLocLon: '126.45',
    });
  };

  const fetchLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('fetchLogout ', error);
    }
  };

  const onLogout = async () => {
    onInitialize();
    fetchLogout();
    await persistor.purge();
    sessionStorage.setItem('isAuthenticated', 'false');
    navigate(`/`, { replace: true });
  };

  const onCenter = () => {
    if (isCenter) {
      onInitialize();
      navigate('/manager');
    } else {
      navigate('/center');
    }
  };

  return (
    <CenterHeaderTemplate>
      <LogoImage src={process.env.PUBLIC_URL + '/images/img_logo.svg'} alt="Logo" />
      <HeaderText>{title}</HeaderText>

      <ConnectionStatus isConnected={soritok.isConnected}>
        {soritok.isConnected ? '● Connected' : '○ Disconnected'}
      </ConnectionStatus>

      <button
        onClick={handleReset}
        disabled={!soritok.isConnected}
        style={{
          padding: '5px 10px',
          backgroundColor: '#d30031',
          color: '#fff',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer',
          marginLeft: '20px',
        }}
      >
        가상 소실 발생 (모달 띄우기)
      </button>

      <HorizontalWrap>
        <InfoText>{userinfo?.belong}</InfoText>
        <InfoText>{userinfo?.userName}</InfoText>
      </HorizontalWrap>
      <ManagerButton onClick={onCenter}>
        {isCenter ? (
          <Image src={process.env.PUBLIC_URL + '/images/img_manager.svg'} alt="Manager" />
        ) : (
          <Image src={process.env.PUBLIC_URL + '/images/img_center.svg'} alt="Manager" />
        )}
      </ManagerButton>
      <LogoutButton onClick={onLogout}>
        <Image src={process.env.PUBLIC_URL + '/images/btn_logout.svg'} alt="Logout" />
      </LogoutButton>
    </CenterHeaderTemplate>
  );
};

const CenterHeaderTemplate = styled.div`
  position: absolute;
  display: flex;
  width: 100%;
  min-width: 80rem;
  min-height: 3.75rem;
  z-index: 99;
  align-items: center;
  background: linear-gradient(180deg, #1760a6 0%, #010c16 49.64%, #1760a6 100%);
`;
const LogoImage = styled.img`
  margin-left: 1.5rem;
  align-items: center;
  justify-content: center;
  object-fit: cover;
`;
const HeaderText = styled.h1`
  margin-left: 1rem;
  font-style: normal;
  font-weight: 700;
  font-size: 1.5rem;
  line-height: 2rem;
  color: #ffffff;
  text-align: center;
`;
const ConnectionStatus = styled.span<{ isConnected: boolean }>`
  font-size: 0.8rem;
  margin-left: 2rem;
  color: ${(props) => (props.isConnected ? '#4caf50' : '#f44336')};
`;
const HorizontalWrap = styled.div`
  position: absolute;
  right: 25rem;
  display: flex;
`;
const InfoText = styled.div`
  color: #fff;
  font-family: Inter;
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  & + & {
    margin-left: 1.5rem;
  }
`;
const ManagerButton = styled.button`
  position: absolute;
  right: 10rem;
  background: transparent;
  border: none;
  margin-right: 2rem;
  cursor: pointer;
`;
const LogoutButton = styled.button`
  position: absolute;
  right: 0;
  background: transparent;
  border: none;
  margin-right: 2rem;
  cursor: pointer;
`;
const Image = styled.img`
  align-items: center;
  justify-content: center;
  object-fit: cover;
`;

export default CenterHeader;
